import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../../components/AppLayout';
import api from '../../api/axiosInstance';

const HEADER_H = 52;

/* ─── Helpers ────────────────────────────────────────────────────── */
function CatBadge({ cat }) {
  if (!cat) return null;
  if (cat === 'N50')    return <span style={S.badgeN50}>N50</span>;
  if (cat === 'FO')     return <span style={S.badgeFO}>FO</span>;
  return <span style={S.badgeOthers}>OTHERS</span>;
}

/* ─── Main Component ─────────────────────────────────────────────── */
export default function UserWatchlistTracker() {
  /* users */
  const [users, setUsers]           = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch]   = useState('');

  /* watchlists */
  const [watchlists, setWatchlists]   = useState([]);
  const [wlLoading, setWlLoading]     = useState(false);
  const [expandedWL, setExpandedWL]   = useState(null);

  /* watchlist data (market data for selected list) */
  const [dataWLId, setDataWLId]       = useState(null);
  const [stocksData, setStocksData]   = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  const [toast, setToast] = useState({ msg: '', type: 'success' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  /* ── load all users ── */
  useEffect(() => {
    api.get('/admin/users?limit=200')
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => showToast('Failed to load users', 'error'))
      .finally(() => setUsersLoading(false));
  }, []);

  /* ── load watchlists for selected user ── */
  const loadUserWatchlists = useCallback(async (userId) => {
    setWlLoading(true);
    setWatchlists([]);
    setExpandedWL(null);
    setDataWLId(null);
    setStocksData([]);
    try {
      const { data } = await api.get(`/watchlist/user/${userId}`);
      setWatchlists(data.data || []);
    } catch {
      showToast('Failed to load watchlists', 'error');
    } finally {
      setWlLoading(false);
    }
  }, []);

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    loadUserWatchlists(u._id);
  };

  /* ── load market data for a watchlist ── */
  const loadWatchlistData = async (watchlistId) => {
    if (dataWLId === watchlistId) {
      // toggle off
      setDataWLId(null);
      setStocksData([]);
      return;
    }
    setDataWLId(watchlistId);
    setDataLoading(true);
    setStocksData([]);
    try {
      const { data } = await api.get(`/watchlist/${watchlistId}/data`);
      setStocksData(data.data || []);
    } catch {
      showToast('Failed to load market data', 'error');
    } finally {
      setDataLoading(false);
    }
  };

  /* ── filtered users ── */
  const filteredUsers = users.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  /* ─── render ─────────────────────────────────────────────────────── */
  return (
    <AppLayout pageBadge="👁️ User Watchlists">
      <div style={S.bg} />

      {/* Toast */}
      {toast.msg && (
        <div style={{ ...S.toast, background: toast.type === 'error' ? 'rgba(239,68,68,0.95)' : 'rgba(16,185,129,0.95)' }}>
          {toast.msg}
        </div>
      )}

      <div style={S.shell}>
        {/* ── Top bar ── */}
        <div style={S.topBar}>
          <div>
            <div style={S.pageTitle}>👁️ User Watchlist Tracker</div>
            <div style={S.pageSub}>Admin view — browse all user watchlists and market data</div>
          </div>
          <div style={S.topStats}>
            <div style={S.statPill}>
              <span style={{ color: '#a78bfa', fontWeight: 700 }}>{users.length}</span>
              <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: 5 }}>Users</span>
            </div>
            <div style={S.statPill}>
              <span style={{ color: '#00f260', fontWeight: 700 }}>
                {users.reduce((acc, u) => acc, 0)}
              </span>
              {selectedUser && (
                <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: 5 }}>
                  {watchlists.length} watchlists for {selectedUser.name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Content: two-column layout ── */}
        <div style={S.content}>
          {/* Left column: user list */}
          <div style={S.leftPanel}>
            <div style={S.panelHead}>
              <div style={S.panelTitle}>Users</div>
            </div>
            <div style={{ padding: '10px 12px 8px' }}>
              <input
                style={S.searchInput}
                placeholder="🔍 Search users..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
            </div>
            <div style={S.userList}>
              {usersLoading ? (
                <div style={S.listCenter}>
                  <div style={S.spinnerSm} />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={S.listCenter}>No users found</div>
              ) : (
                filteredUsers.map(u => {
                  const isSelected = selectedUser?._id === u._id;
                  return (
                    <div
                      key={u._id}
                      style={{ ...S.userItem, ...(isSelected ? S.userItemActive : {}) }}
                      onClick={() => handleSelectUser(u)}
                    >
                      <div style={{ ...S.userAvatar, background: isSelected ? 'linear-gradient(135deg,#667eea,#764ba2)' : 'rgba(255,255,255,0.08)' }}>
                        {(u.name || u.email || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.email}
                        </div>
                      </div>
                      <span style={{
                        fontSize: '0.65rem', padding: '2px 6px', borderRadius: 4, fontWeight: 700,
                        background: u.role === 'admin' ? 'rgba(239,68,68,0.15)' : u.role === 'analyst' ? 'rgba(251,191,36,0.15)' : 'rgba(102,126,234,0.15)',
                        color: u.role === 'admin' ? '#ef4444' : u.role === 'analyst' ? '#fbbf24' : '#a78bfa',
                      }}>
                        {u.role}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right column: watchlists + data */}
          <div style={S.rightPanel}>
            {!selectedUser ? (
              <div style={S.emptyRight}>
                <div style={{ fontSize: 52, opacity: 0.15, marginBottom: 12 }}>👥</div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.95rem' }}>Select a user to view their watchlists</div>
              </div>
            ) : (
              <>
                {/* User info bar */}
                <div style={S.userInfoBar}>
                  <div style={S.userAvatar2}>{(selectedUser.name || '?')[0].toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{selectedUser.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)' }}>{selectedUser.email}</div>
                  </div>
                  <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>
                    {wlLoading ? '...' : `${watchlists.length} watchlist${watchlists.length !== 1 ? 's' : ''}`}
                  </span>
                </div>

                {/* Watchlists */}
                {wlLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <div style={S.spinner} />
                  </div>
                ) : watchlists.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.35)', fontSize: '0.9rem' }}>
                    This user has no watchlists.
                  </div>
                ) : (
                  <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                    {watchlists.map(wl => {
                      const isDataOpen = dataWLId === wl.watchlistId;
                      return (
                        <div key={wl.watchlistId} style={S.wlCard}>
                          {/* Color strip */}
                          <div style={{ height: 3, background: wl.color, borderRadius: '8px 8px 0 0' }} />
                          <div style={{ padding: '12px 14px' }}>
                            {/* Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                              <div>
                                <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>{wl.watchlistName}</div>
                                {wl.description && (
                                  <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{wl.description}</div>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={S.stockCount}>{wl.stocks?.length || 0} stocks</span>
                                <div style={{ width: 20, height: 20, borderRadius: 4, background: wl.color }} />
                              </div>
                            </div>

                            {/* Stock chips */}
                            {wl.stocks?.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                                {wl.stocks.slice(0, 12).map(st => (
                                  <span key={st.symbol} style={S.stockChip}>{st.symbol}</span>
                                ))}
                                {wl.stocks.length > 12 && (
                                  <span style={{ ...S.stockChip, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}>
                                    +{wl.stocks.length - 12} more
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Load market data button */}
                            {wl.stocks?.length > 0 && (
                              <button
                                style={{ ...S.btnViewData, ...(isDataOpen ? S.btnViewDataActive : {}) }}
                                onClick={() => loadWatchlistData(wl.watchlistId)}
                              >
                                {dataLoading && isDataOpen ? '⟳ Loading...' : isDataOpen ? '▲ Hide Market Data' : '▼ View Market Data'}
                              </button>
                            )}

                            {/* Market data table */}
                            {isDataOpen && (
                              <div style={{ marginTop: 12 }}>
                                {dataLoading ? (
                                  <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
                                    <div style={S.spinnerSm} />
                                  </div>
                                ) : stocksData.length === 0 ? (
                                  <div style={{ textAlign: 'center', padding: '1rem', color: 'rgba(255,255,255,0.35)', fontSize: '0.83rem' }}>
                                    No market data available.
                                  </div>
                                ) : (
                                  <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                      <thead>
                                        <tr>
                                          {['Symbol', 'Name', 'Cat', 'Price', 'Chng%', 'Entry', 'Target', 'S/L', 'P&L%', 'Signals'].map(h => (
                                            <th key={h} style={S.miniTh}>{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {stocksData.map(s => {
                                          const pct = s.percentChange;
                                          const pl  = s.profitLoss;
                                          return (
                                            <tr key={s.symbol}>
                                              <td style={S.miniTd}><strong style={{ color: '#fff' }}>{s.symbol}</strong></td>
                                              <td style={{ ...S.miniTd, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.55)' }}>
                                                {s.stockName}
                                              </td>
                                              <td style={S.miniTd}><CatBadge cat={s.dataSource} /></td>
                                              <td style={{ ...S.miniTd, textAlign: 'right', fontWeight: 600 }}>
                                                {s.currentPrice ? `₹${s.currentPrice.toFixed(2)}` : '—'}
                                              </td>
                                              <td style={{ ...S.miniTd, textAlign: 'right', fontWeight: 700, color: pct > 0 ? '#00f260' : pct < 0 ? '#ff6b6b' : 'rgba(255,255,255,0.4)' }}>
                                                {pct !== null && pct !== undefined ? `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%` : '—'}
                                              </td>
                                              <td style={{ ...S.miniTd, textAlign: 'right' }}>
                                                {s.entryPrice ? `₹${s.entryPrice.toFixed(2)}` : '—'}
                                              </td>
                                              <td style={{ ...S.miniTd, textAlign: 'right', color: '#43e97b' }}>
                                                {s.targetPrice ? `₹${s.targetPrice.toFixed(2)}` : '—'}
                                              </td>
                                              <td style={{ ...S.miniTd, textAlign: 'right', color: '#ff6b6b' }}>
                                                {s.stopLoss ? `₹${s.stopLoss.toFixed(2)}` : '—'}
                                              </td>
                                              <td style={{ ...S.miniTd, textAlign: 'right', fontWeight: 700, color: pl !== null && pl !== undefined ? (parseFloat(pl) >= 0 ? '#00f260' : '#ff6b6b') : 'rgba(255,255,255,0.3)' }}>
                                                {pl !== null && pl !== undefined ? `${parseFloat(pl) >= 0 ? '+' : ''}${pl}%` : '—'}
                                              </td>
                                              <td style={{ ...S.miniTd, textAlign: 'center' }}>
                                                <span style={{
                                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                  minWidth: 22, height: 22, borderRadius: 5, fontWeight: 700, fontSize: '0.75rem', color: '#fff',
                                                  background: s.strategySignals?.totalSignals > 0 ? 'linear-gradient(135deg,#667eea,#764ba2)' : 'rgba(255,255,255,0.07)',
                                                }}>
                                                  {s.strategySignals?.totalSignals || 0}
                                                </span>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const S = {
  bg: {
    position: 'fixed', inset: 0, zIndex: 0,
    background: 'linear-gradient(135deg, #0d0d1a 0%, #111827 50%, #0a1628 100%)',
    pointerEvents: 'none',
  },
  shell: {
    position: 'relative', zIndex: 1,
    height: `calc(100vh - ${HEADER_H}px)`,
    marginTop: HEADER_H,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  toast: {
    position: 'fixed', top: 72, right: 24, zIndex: 9999,
    color: '#fff', padding: '0.75rem 1.25rem', borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)', fontSize: '0.9rem',
  },
  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0, flexWrap: 'wrap', gap: 10,
  },
  pageTitle: {
    fontSize: '1.2rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  pageSub: { fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', marginTop: 2 },
  topStats: { display: 'flex', gap: 10, alignItems: 'center' },
  statPill: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 8, padding: '5px 12px', fontSize: '0.83rem',
    display: 'flex', alignItems: 'center',
  },
  content: {
    flex: 1, minHeight: 0,
    display: 'flex', flexDirection: 'row',
    overflow: 'hidden',
  },

  /* ── Left panel ── */
  leftPanel: {
    width: 260, flexShrink: 0,
    borderRight: '1px solid rgba(255,255,255,0.07)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  panelHead: {
    padding: '12px 14px 8px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0,
  },
  panelTitle: { fontWeight: 700, fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)' },
  searchInput: {
    width: '100%', padding: '7px 10px', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, color: '#fff', fontSize: '0.82rem', outline: 'none',
  },
  userList: { flex: 1, minHeight: 0, overflowY: 'auto' },
  listCenter: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 100, color: 'rgba(255,255,255,0.35)', fontSize: '0.82rem',
  },
  userItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 12px', cursor: 'pointer',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    transition: 'background 0.15s',
  },
  userItemActive: {
    background: 'rgba(102,126,234,0.12)',
    borderLeft: '3px solid #667eea',
  },
  userAvatar: {
    width: 34, height: 34, borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: '0.9rem', color: '#fff', flexShrink: 0,
  },

  /* ── Right panel ── */
  rightPanel: {
    flex: 1, minWidth: 0,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  emptyRight: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  userInfoBar: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px', background: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0,
  },
  userAvatar2: {
    width: 40, height: 40, borderRadius: 10,
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: '1.1rem', color: '#fff', flexShrink: 0,
  },

  /* ── Watchlist cards ── */
  wlCard: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, marginBottom: 12, overflow: 'hidden',
  },
  stockCount: {
    fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20,
    background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.55)',
  },
  stockChip: {
    fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4,
    background: 'rgba(102,126,234,0.15)', color: '#a78bfa',
    border: '1px solid rgba(102,126,234,0.2)', fontWeight: 600,
  },
  btnViewData: {
    fontSize: '0.78rem', padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.7)', fontWeight: 600,
  },
  btnViewDataActive: {
    background: 'rgba(102,126,234,0.15)', color: '#a78bfa',
    borderColor: 'rgba(102,126,234,0.3)',
  },
  miniTh: {
    padding: '6px 10px', textAlign: 'left', fontSize: '0.72rem',
    fontWeight: 700, color: 'rgba(255,255,255,0.6)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(0,0,0,0.2)',
    position: 'sticky', top: 0,
  },
  miniTd: {
    padding: '6px 10px', fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  },

  /* ── Badges ── */
  badgeN50: {
    padding: '1px 6px', borderRadius: 3, fontSize: '0.62rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#00f260,#0575e6)', color: '#fff',
  },
  badgeFO: {
    padding: '1px 6px', borderRadius: 3, fontSize: '0.62rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#4facfe,#00f2fe)', color: '#fff',
  },
  badgeOthers: {
    padding: '1px 6px', borderRadius: 3, fontSize: '0.62rem', fontWeight: 700,
    background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)',
    border: '1px solid rgba(255,255,255,0.15)',
  },

  /* ── Misc ── */
  spinner: {
    width: 36, height: 36,
    border: '3px solid rgba(255,255,255,0.07)',
    borderTop: '3px solid #667eea',
    borderRadius: '50%', animation: 'spin 1s linear infinite',
  },
  spinnerSm: {
    width: 22, height: 22,
    border: '2px solid rgba(255,255,255,0.07)',
    borderTop: '2px solid #667eea',
    borderRadius: '50%', animation: 'spin 1s linear infinite',
  },
};
